import { MagnifyingGlassIcon, XIcon } from '@phosphor-icons/react';
import {
  Button as AriaButton,
  Input,
  SearchField as AriaSearchField,
  type SearchFieldProps as AriaSearchFieldProps,
} from 'react-aria-components';
import { cn } from './lib/cn';
import { INPUT_RESET } from './lib/input-reset';
import { Spinner } from './Spinner';

export type SearchFieldProps = Omit<
  AriaSearchFieldProps,
  'className' | 'children'
> & {
  className?: string;
  placeholder?: string;
  /** Swaps the magnifier for a spinner while results for the query load. */
  isLoading?: boolean;
};

/**
 * Pill search input. React Aria wires up the type-ahead semantics and the clear
 * button, including Escape to clear.
 *
 * The clear button hides on `data-empty`, which React Aria sets on the field
 * itself — hence the `group` here rather than a value check in render.
 */
export function SearchField({
  className,
  placeholder = 'Search…',
  'aria-label': ariaLabel = 'Search',
  isLoading = false,
  ...props
}: SearchFieldProps) {
  return (
    <AriaSearchField
      {...props}
      aria-label={ariaLabel}
      className={cn(
        'sk:group sk:inline-flex sk:h-7 sk:w-55 sk:items-center sk:gap-1.5 sk:rounded-full',
        'sk:inset-shadow-hairline sk:bg-surface sk:px-3 sk:shadow-card',
        'sk:transition-[box-shadow] sk:duration-75',
        'sk:focus-within:shadow-none',
        className,
      )}
    >
      {isLoading ? (
        <Spinner
          size="sm"
          label="Searching"
          className="sk:text-fg-secondary sk:shrink-0"
        />
      ) : (
        <MagnifyingGlassIcon
          size={12}
          weight="bold"
          className="sk:text-fg-secondary sk:shrink-0"
        />
      )}

      <Input
        placeholder={placeholder}
        className={cn(
          INPUT_RESET,
          'sk:text-fg sk:min-w-0 sk:flex-1 sk:bg-transparent sk:text-dense sk:font-medium sk:tracking-dense',
          'sk:outline-none sk:placeholder:font-normal sk:placeholder:text-fg-tertiary',
          'sk:[&::-webkit-search-cancel-button]:hidden',
          // 16px stops iOS Safari zooming the page when the field takes focus.
          'sk:max-sm:text-base',
        )}
      />

      <AriaButton
        className={cn(
          /* Undoes the user-agent button styling: no Preflight here. */
          'sk:m-0 sk:appearance-none sk:border-0 sk:p-0 sk:font-sans sk:cursor-pointer',
          'sk:bg-fill-secondary sk:text-fg-secondary sk:inline-flex sk:size-5 sk:shrink-0 sk:items-center sk:justify-center',
          'sk:rounded-full sk:outline-none sk:group-data-[empty]:hidden',
          'sk:hover:text-fg sk:rac-focus-visible:outline-2 sk:rac-focus-visible:outline-solid sk:rac-focus-visible:outline-offset-1 sk:rac-focus-visible:outline-primary',
        )}
      >
        <XIcon size={12} weight="bold" />
      </AriaButton>
    </AriaSearchField>
  );
}
